import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { checkCooldown, formatCooldownTime } from '../../utils/eventCooldown';
import { isEventNotFinishedYet } from '../../utils/eventSchedule';
import { EventCardIcon } from '../discover/EventCardIcons';
import './EventQuickActions.css';

/**
 * Komponent paska szybkich akcji w footerze eventu
 * Pokazuje odpowiednie akcje w zależności od statusu użytkownika
 */
function EventQuickActions({
  eventId,
  event,
  userApplication,
  isParticipant,
  isHost,
  participantsCount,
  maxParticipants,
  onStatusChange
}) {
  const { t } = useTranslation('events');
  const [loading, setLoading] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Automatyczne zmniejszanie cooldown z czasem
  useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const interval = setInterval(() => {
      const lastActionTime = userApplication?.updated_at;
      const { secondsRemaining } = checkCooldown(lastActionTime);
      setCooldownRemaining(secondsRemaining);

      if (secondsRemaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownRemaining, userApplication]);

  const availableSpots = maxParticipants - participantsCount;
  const isFull = availableSpots <= 0;
  const isCancelled = event?.is_cancelled || false;
  const eventIsOpen = isEventNotFinishedYet(event);

  // Określ status użytkownika
  const getUserStatus = () => {
    if (isHost) {
      return 'host';
    }
    if (isParticipant) {
      return 'participant';
    }
    if (userApplication?.status === 'pending') {
      return 'pending';
    }
    if (userApplication?.status === 'rejected') {
      return 'rejected';
    }
    return 'none';
  };

  const status = getUserStatus();

  const handleJoin = async () => {
    if (!eventIsOpen) {
      alert(t('events.join.eventClosed'));
      return;
    }

    // Sprawdź czy wydarzenie jest anulowane
    if (event?.is_cancelled) {
      alert(t('events.eventCancelled', 'Wydarzenie zostało anulowane'));
      return;
    }

    // Sprawdź cooldown
    const lastActionTime = userApplication?.updated_at;
    const { canPerform, secondsRemaining } = checkCooldown(lastActionTime);
    
    if (!canPerform) {
      alert(t('events.join.cooldown', { seconds: formatCooldownTime(secondsRemaining) }));
      setCooldownRemaining(secondsRemaining);
      return;
    }

    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert(t('events.join.notLoggedIn'));
        return;
      }

      // Sprawdź czy już ma zgłoszenie
      if (userApplication) {
        if (userApplication.status === 'pending') {
          alert(t('events.join.alreadyApplied'));
          return;
        }
        
        // Zaktualizuj istniejące zgłoszenie
        const { error } = await supabase
          .from('event_applications')
          .update({
            status: 'pending',
            updated_at: new Date().toISOString()
          })
          .eq('id', userApplication.id);

        if (error) throw error;
      } else {
        // Sprawdź czy rekord już istnieje
        const { data: existingApplication } = await supabase
          .from('event_applications')
          .select('id')
          .eq('event_id', eventId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (existingApplication) {
          const { error } = await supabase
            .from('event_applications')
            .update({
              status: 'pending',
              updated_at: new Date().toISOString()
            })
            .eq('id', existingApplication.id);

          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('event_applications')
            .insert({
              event_id: eventId,
              user_id: user.id,
              status: 'pending'
            });

          if (error) throw error;
        }
      }

      if (onStatusChange) {
        onStatusChange();
      }
    } catch (err) {
      console.error('Error joining event:', err);
      alert(err.message || t('events.join.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!eventIsOpen) {
      alert(t('events.leave.eventClosed'));
      return;
    }

    if (!window.confirm(t('events.leave.confirm'))) {
      return;
    }

    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert(t('events.join.notLoggedIn'));
        return;
      }

      // Jeśli jest uczestnikiem, usuń z uczestników
      if (isParticipant) {
        await supabase
          .from('event_participants')
          .update({ status: 'withdrawn', updated_at: new Date().toISOString() })
          .eq('event_id', eventId)
          .eq('user_id', user.id)
          .eq('status', 'active');
      }

      // Zaktualizuj zgłoszenie
      if (userApplication) {
        await supabase
          .from('event_applications')
          .update({ status: 'withdrawn', updated_at: new Date().toISOString() })
          .eq('id', userApplication.id);
      }

      if (onStatusChange) {
        onStatusChange();
      }
    } catch (err) {
      console.error('Error withdrawing application:', err);
      alert(err.message || t('events.leave.error'));
    } finally {
      setLoading(false);
    }
  };

  const spotsLeftText = `(${availableSpots} ${t('events.page.footer.spotsLeft')})`;

  // Host - pokaż tylko badge
  if (status === 'host') {
    return (
      <div className="event-quick-actions">
        <div className="event-quick-actions-info">
          <span className="event-quick-actions-participants">
            <strong>{participantsCount}/{maxParticipants}</strong>
            <span className="event-quick-actions-spots">{spotsLeftText}</span>
          </span>
        </div>
        <span className="event-quick-actions-badge host">
          <EventCardIcon type="host" className="event-quick-actions-badge-icon" size={15} />
          {t('events.page.footer.youAreHost')}
        </span>
      </div>
    );
  }

  if (!eventIsOpen) {
    return (
      <div className="event-quick-actions">
        <div className="event-quick-actions-info">
          <span className="event-quick-actions-participants">
            <strong>{participantsCount}/{maxParticipants}</strong>
            <span className="event-quick-actions-spots">{spotsLeftText}</span>
          </span>
        </div>
        <span className="event-quick-actions-badge ended">
          <EventCardIcon type="clock" className="event-quick-actions-badge-icon" size={15} />
          {t('events.status.ended', 'Zakończone')}
        </span>
      </div>
    );
  }

  // Uczestnik - pokaż status uczestnictwa
  if (status === 'participant') {
    return (
      <div className="event-quick-actions">
        <div className="event-quick-actions-info">
          <span className="event-quick-actions-participants">
            <strong>{participantsCount}/{maxParticipants}</strong>
            <span className="event-quick-actions-spots">{spotsLeftText}</span>
          </span>
        </div>
        <span className="event-quick-actions-badge participant">
          <EventCardIcon type="participant" className="event-quick-actions-badge-icon" size={15} />
          {t('events.page.footer.youAreParticipant')}
        </span>
      </div>
    );
  }

  // Oczekujące zgłoszenie - pokaż status pending
  if (status === 'pending') {
    return (
      <div className="event-quick-actions">
        <div className="event-quick-actions-info">
          <span className="event-quick-actions-participants">
            <strong>{participantsCount}/{maxParticipants}</strong>
          </span>
        </div>
        <div className="event-quick-actions-status">
          <span className="event-quick-actions-badge pending">
            <EventCardIcon type="hourglass" className="event-quick-actions-badge-icon" size={15} />
            {t('events.join.pending')}
          </span>
          <button
            type="button"
            className="event-quick-actions-button withdraw"
            onClick={handleWithdraw}
            disabled={loading || cooldownRemaining > 0}
          >
            {t('events.join.withdraw')}
          </button>
        </div>
      </div>
    );
  }

  // Brak zgłoszenia - pokaż przycisk dołącz (lub komunikat o anulowaniu)
  if (isCancelled) {
    return (
      <div className="event-quick-actions">
        <div className="event-quick-actions-info">
          <span className="event-quick-actions-participants">
            <strong>{participantsCount}/{maxParticipants}</strong>
          </span>
        </div>
        <span className="event-quick-actions-badge cancelled">
          <EventCardIcon type="alert" className="event-quick-actions-badge-icon" size={15} />
          {t('events.eventCancelled', 'Wydarzenie zostało anulowane')}
        </span>
      </div>
    );
  }

  return (
    <div className="event-quick-actions">
      <div className="event-quick-actions-info">
        <span className="event-quick-actions-participants">
          <strong>{participantsCount}/{maxParticipants}</strong>
          <span className="event-quick-actions-spots">{spotsLeftText}</span>
        </span>
      </div>
      <button
        type="button"
        className="event-quick-actions-button join"
        onClick={handleJoin}
        disabled={loading || isFull || cooldownRemaining > 0 || isCancelled || !eventIsOpen}
      >
        <EventCardIcon type="users" className="event-quick-actions-button-icon" size={16} />
        {loading ? t('events.join.joining') : t('events.join.button')}
      </button>
    </div>
  );
}

export default EventQuickActions;
